import { Controller, useFormContext } from "react-hook-form";
import Edit from "@mui/icons-material/Edit";
import Icon from "@/core/components/Icon/Icon";
import { SelectField as ReusableSelectField } from "@flightright/reusable-components";
import { Box, Button, Typography } from "@mui/material";
import {
  FieldSelectOption,
  HeadingLevel,
  MaterialColors,
} from "@/core/types/types";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { Theme } from "@mui/material";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { useEffect, useState } from "react";

const styles = (theme: Theme) => ({
  bordered: {
    borderLeft: "2px solid " + theme.palette.grey.A700,
    marginLeft: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
});

interface DescriptionalSelectField {
  id: string;
  controllerName: string;
  label: string;
  options: FieldSelectOption[];
  initialValue?: string;
}

export const DescriptionalSelectField = ({
  id,
  controllerName,
  label,
  options,
  initialValue,
}: DescriptionalSelectField) => {
  const classes: any = useCustomStyles(styles);
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    clearErrors,
  } = useFormContext();
  const [valueIsDirty, setValueIsDirty] = useState<boolean>(false);
  const value: string | undefined = getValues(controllerName);
  const lowerCaseValue: string | undefined = value?.toLocaleLowerCase();
  const error: string = !!errors[controllerName]
    ? (errors[controllerName]?.message as string)
    : "";

  const handleValueUpdate = (updatedValue: string | undefined) => {
    clearErrors(controllerName);
    setValue(controllerName, updatedValue);
  };

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;

    handleValueUpdate(value);
    setValueIsDirty(true);
  };

  useEffect(() => {
    if (initialValue) {
      handleValueUpdate(initialValue);
    }
  }, [initialValue]);

  const valueIsPreselected = value === initialValue && !valueIsDirty;
  const flightProblemLabel = `${label}${
    valueIsPreselected ? ".preselected" : ""
  }`;

  return (
    <>
      {value ? (
        <Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant={HeadingLevel.H3}>
              {t(flightProblemLabel)}
            </Typography>
            <Button
              variant="text"
              color={MaterialColors.Primary}
              sx={{ cursor: "pointer" }}
              onClick={(e) => {
                handleChange(e as unknown as SelectChangeEvent);
              }}
            >
              <Edit />
            </Button>
          </Box>
          <Box mb={2}>
            <Typography>{t(`flightProblem.${lowerCaseValue}`)}</Typography>
          </Box>
          <Box display="flex">
            <Icon
              src={`images/icons/flight-problems/${lowerCaseValue}.svg`}
              alt="flight-problem-icon"
            />
            <Box sx={classes.bordered}>
              <Typography variant={HeadingLevel.H3}>
                {t(`flightProblem.${lowerCaseValue}`)}
              </Typography>
              <Typography>
                {t(`flightProblem.${lowerCaseValue}.description`)}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Controller
          name={controllerName}
          control={control}
          rules={{
            required: {
              value: true,
              message: t("validate.flightProblem.required.message"),
            },
          }}
          render={() => (
            <ReusableSelectField
              id={id}
              label={t(label)}
              options={options}
              value={value ?? ""}
              handleChange={handleChange}
              error={error}
              {...extraProps}
            />
          )}
        />
      )}
    </>
  );
};
